import React from 'react';
import PropTypes from 'prop-types';
import { getTenantConfig } from "utils/tenant";
import { SFEntityID, SFSlots } from 'utils/types';
import css from './Schedule.css';
import Filter from './Filter';
import SlotsList from './SlotsList';
import { Button } from 'semantic-ui-react';
import NoSlotsFound from './NoSlotsFound';
import AppointmentTimeSlot from "./AppointmentTimeSlot";
import SlotTypeDropdown from "./SlotTypeDropdown";

const enableGroupFilterText = 'There are only group sessions available at the times you’ve selected. To see all available appointments, please choose "All session types" above.';
const enableSingleFilterText = 'There are only single sessions available at the times you’ve selected. To see all available appointments, please choose "All session types" above.';

export default class Schedule extends React.Component {

  componentDidUpdate(prevProps) {
    const allowedSlotsPerClick = getTenantConfig('STUDENT_SCHEDULING_SLOT_DISPLAY_NUM') || 10;
    if ( prevProps !== null && prevProps.numberOfVisibleSlots !== this.props.numberOfVisibleSlots &&
      this.props.numberOfVisibleSlots > allowedSlotsPerClick ) {
      const slotToFocus = this.props.numberOfVisibleSlots - allowedSlotsPerClick;
      document.querySelectorAll('input[name=slot-radio-form]')[slotToFocus].focus();
    }
  }

  get slotsFilteredByType() {
      if (this.props.showSingleSessions && this.props.showGroupSessions) return this.props.slots;
      if (this.props.showSingleSessions && !this.props.showGroupSessions) return this.props.slots.filter(({scheduleBlock}) => !scheduleBlock.groupSession);
      if (!this.props.showSingleSessions && this.props.showGroupSessions) return this.props.slots.filter(({scheduleBlock}) => scheduleBlock.groupSession);
      return null
  }

  get showServiceProviderInfo() {
    return getTenantConfig('SERVICE_APPT_SHOW_PROVIDER') || !this.props.isService;
  }

  convertToTimeSlots(slots) {
    return slots.map(slot => new AppointmentTimeSlot(
      slot,
      this.props.isService,
      this.showServiceProviderInfo,
      this.props.recurring));
  }

  get visibleSlots() {
    return this.slotsFilteredByType.filter((slots, i) => i < this.props.numberOfVisibleSlots);
  }

  get showMoreButton() {
    // Do not show button if number of visible slots is greater than the number of slots after
    // being filtered by the "Group Meetings" checkbox
    if ( this.props.numberOfVisibleSlots >= this.slotsFilteredByType.length ) return null;
    return (
      <div className={css['button-container']}>
        <Button onClick={this.props.onShowMoreSlotsButtonClick}>
          Show more times
        </Button>
      </div>
    );
  }

  get sessionTypeFilter() {
    if ( this.props.slots === null || this.props.slots.length === 0 ) return null;
    return <SlotTypeDropdown onSlotTypeDropdownChanged={this.props.onSlotTypeDropdownChanged}/>;
  }

  get slotsList() {
    if ( this.props.slots === null || this.props.slots.length === 0 ) return <NoSlotsFound />;

    if (!this.props.showGroupSessions && !this.props.slots.find(slot => !slot.scheduleBlock.groupSession)) {
      return (<NoSlotsFound message={enableGroupFilterText} />);
    }

    if (!this.props.showSingleSessions && !this.props.slots.find(slot => slot.scheduleBlock.groupSession)) {
      return (<NoSlotsFound message={enableSingleFilterText} />);
    }

    return (
      <React.Fragment>
        <SlotsList
          allSlots={this.convertToTimeSlots(this.props.slots)}
          visibleSlots={this.convertToTimeSlots(this.visibleSlots)}
          slotStartDate={this.props.slotStartDate}
          slotOwnerId={this.props.slotOwnerId}
          onSlotSelected={this.props.onSlotSelected}
          recurring={this.props.recurring}
          isService={this.props.isService}
          currentDuration={this.props.currentDuration}
          locationName={this.props.locationName}
        />
        {this.showMoreButton}
      </React.Fragment>
    );
  }

  render() {
    return (
      <div className={css['container']}>
        <Filter
          onSlotTypeDropdownChanged={this.props.onSlotTypeDropdownChanged}
          startDate={this.props.filterStartDate}
          onStartDateChanged={this.props.onFilterStartDateChanged}
          endDate={this.props.filterEndDate}
          onEndDateChanged={this.props.onFilterEndDateChanged}
        />
        <div className={css['slots-container']}>
          {this.sessionTypeFilter}
          {this.slotsList}
        </div>
      </div>
    );
  }
}

Schedule.propTypes = {
  slots: SFSlots.isRequired,
  recurring: PropTypes.bool,
  filterStartDate: PropTypes.object.isRequired,
  filterEndDate: PropTypes.object.isRequired,
  numberOfVisibleSlots: PropTypes.number.isRequired,
  onShowMoreSlotsButtonClick: PropTypes.func.isRequired,
  slotStartDate: PropTypes.object,
  slotOwnerId: SFEntityID,
  onSlotSelected: PropTypes.func.isRequired,
  showGroupSessions: PropTypes.bool.isRequired,
  showSingleSessions: PropTypes.bool.isRequired,
  onSlotTypeDropdownChanged: PropTypes.func.isRequired,
  onFilterStartDateChanged: PropTypes.func.isRequired,
  onFilterEndDateChanged: PropTypes.func.isRequired,
  isService: PropTypes.bool.isRequired,
  currentDuration: PropTypes.number,
  locationName: PropTypes.string
};

Schedule.defaultProps = {
  recurring: false
};
